import React from "react"

import { Link, usePathname } from "expo-router"
import PropTypes from "prop-types"

import {
  Badge,
  BadgeText,
  Box,
  HStack,
  Icon,
  Pressable,
  Text,
  Tooltip,
  TooltipContent,
  TooltipText,
  VStack,
} from "wealthmode-frontend/gluestack/ui"

import {
  SIDEBAR_ICON_CLASS,
  SIDEBAR_ICON_SLOT_CLASS,
  SIDEBAR_NAV_ROW_HEIGHT_CLASS,
  SIDEBAR_ROW_INSET_CLASS,
  SIDEBAR_ROW_INSET_EXPANDED_CLASS,
  SIDEBAR_ROW_RADIUS_CLASS,
  SIDEBAR_SECTION_TITLE_HEIGHT_CLASS,
  useSidebar,
} from "@/context"

const NAV_ROW_CLASS = `group flex flex-row items-center shrink-0 transition-colors ${SIDEBAR_NAV_ROW_HEIGHT_CLASS} ${SIDEBAR_ROW_RADIUS_CLASS}`

/**
 * Active rows are a tinted pill rather than a left border: the border had to sit
 * outside the row's rounded corners, which read as a stray tick against the
 * sidebar edge.
 */
const rowStateClass = (isActive: boolean) => (isActive ? "bg-lining-focused/15" : "hover:bg-background-tertiary/60")

// @ts-ignore
export const AppSidebarNav = ({ items }) => {
  const pathname = usePathname()
  const { sidebarUnfoldable: collapsed } = useSidebar()

  const rowInset = collapsed ? SIDEBAR_ROW_INSET_CLASS : SIDEBAR_ROW_INSET_EXPANDED_CLASS

  const wrapWithTooltip = (name: string, renderTrigger: (triggerProps: Record<string, unknown>) => React.ReactNode) => {
    if (!collapsed || !name) {
      return renderTrigger({})
    }

    return (
      <Tooltip
        placement="right"
        offset={2}
        trigger={(triggerProps: Record<string, unknown>) => renderTrigger(triggerProps)}
      >
        <TooltipContent className="bg-background-tertiary border border-border-dark">
          <TooltipText className="text-foreground-primary">{name}</TooltipText>
        </TooltipContent>
      </Tooltip>
    )
  }

  // @ts-ignore
  const navLink = (
    name: string,
    IconComponent: any,
    badge?: { color: string; text: string },
    indent?: boolean,
    isActive?: boolean,
  ) => {
    const iconColor = isActive
      ? "text-lining-focused"
      : "text-foreground-tertiary group-hover:text-foreground-secondary"

    return (
      <HStack className="items-center gap-3 flex-1 min-w-0">
        {IconComponent ? (
          <Box className={`${SIDEBAR_ICON_SLOT_CLASS} shrink-0 items-center justify-center`}>
            <Icon
              as={IconComponent}
              className={`${iconColor} ${SIDEBAR_ICON_CLASS}`}
            />
          </Box>
        ) : indent ? (
          <Box className={`${SIDEBAR_ICON_SLOT_CLASS} items-center justify-center shrink-0`}>
            <Box className={`w-2 h-2 rounded-full ${isActive ? "bg-lining-focused" : "bg-foreground-tertiary"}`} />
          </Box>
        ) : null}
        {collapsed ? null : (
          <Text
            className={`text-sm flex-1 min-w-0 ${
              isActive
                ? "text-foreground-primary font-medium"
                : "text-foreground-secondary group-hover:text-foreground-primary"
            }`}
          >
            {name}
          </Text>
        )}
        {!collapsed && badge && (
          <Badge
            action={badge.color as any}
            className="ml-auto shrink-0"
          >
            <BadgeText>{badge.text}</BadgeText>
          </Badge>
        )}
      </HStack>
    )
  }

  const itemClassName = (isActive: boolean) => `${NAV_ROW_CLASS} ${rowInset} ${rowStateClass(isActive)}`

  // @ts-ignore
  const navItem = (item: any, index: number, indent = false) => {
    const { component, name, badge, icon, ...rest } = item
    const isActive = pathname === rest.to || pathname === rest.href

    const linkContent = navLink(name, icon, badge, indent, isActive)

    return (
      <Box key={index}>
        {rest.to || rest.href ? (
          wrapWithTooltip(name, (triggerProps) => (
            <Link
              {...triggerProps}
              href={rest.to || rest.href}
              className={`${itemClassName(isActive)} w-full`}
            >
              {linkContent}
            </Link>
          ))
        ) : (
          <Box className={itemClassName(false)}>{linkContent}</Box>
        )}
      </Box>
    )
  }

  // @ts-ignore
  const navGroup = (item: any, index: number) => {
    const { component, name, icon, items: subItems } = item
    const [isOpen, setIsOpen] = React.useState(false)

    return (
      <Box key={index}>
        {wrapWithTooltip(name, (triggerProps) => (
          <Pressable
            {...triggerProps}
            onPress={() => setIsOpen(!isOpen)}
            className={`${NAV_ROW_CLASS} ${rowInset} ${rowStateClass(false)}`}
          >
            {navLink(name, icon, undefined, false, false)}
          </Pressable>
        ))}
        {!collapsed && isOpen && (
          <VStack className="ml-4">
            {// @ts-ignore
            subItems?.map((subItem: any, idx: number) =>
              subItem.items ? navGroup(subItem, idx) : navItem(subItem, idx, true),
            )}
          </VStack>
        )}
      </Box>
    )
  }

  // @ts-ignore
  const navTitle = (item: any, index: number) => {
    return (
      <Box
        key={index}
        className={`${rowInset} ${SIDEBAR_SECTION_TITLE_HEIGHT_CLASS} shrink-0 justify-center mt-4`}
      >
        {/* Collapsed has no room for the label, but the grouping still reads as a rule. */}
        {collapsed ? (
          <Box className="h-px bg-border-medium" />
        ) : (
          <Text className="text-xs font-semibold text-foreground-tertiary uppercase tracking-wider leading-none">
            {item.name}
          </Text>
        )}
      </Box>
    )
  }

  return (
    <Box className="flex-1 overflow-y-auto overflow-x-hidden py-4">
      <VStack className="gap-1">
        {items &&
          items.map((item: any, index: number) => {
            if (item.title) return navTitle(item, index)
            if (item.items) return navGroup(item, index)
            return navItem(item, index)
          })}
      </VStack>
    </Box>
  )
}

AppSidebarNav.propTypes = {
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
}
